import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { Agent } from "../../model_gen/agent_base";
import { AgentService } from "../../services/AgentService";
import { AgentActionService } from "../../services/AgentActionService";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";

import { AlertCircleIcon, CheckCircleIcon, ArrowLeftIcon, UploadIcon, ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { SchemaProperty } from "./SchemaPropertyEditor";

// Reuse interfaces from APIImportModal
interface Parameter {
  name: string;
  in: "path" | "query";
  type: string;
  required?: boolean;
  description?: string;
  defaultValue?: any;
  enum?: string[];
}

interface APIActionConfig {
  endpoint?: string;
  method?: string;
  headers?: { [key: string]: string };
  timeout?: number;
  parameters?: Parameter[];
  requestSchema?: { [key: string]: SchemaProperty };
  responseSchemas?: { [statusCode: string]: { [key: string]: SchemaProperty } };
  auth?: {
    type?: string;
    token?: string;
    header?: string;
    key?: string;
  };
}

interface ParsedOperation {
  method: string;
  path: string;
  operationId?: string;
  summary?: string;
  description?: string;
  parameters?: any[];
  requestBody?: any;
  responses?: any;
  selected?: boolean;
  actionName?: string;
  actionDescription?: string;
}

const BulkAPIImportPage: React.FC = () => {
  const { apiKey } = usePermissions();
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  
  const [agent, setAgent] = useState<Agent | null>(null);
  const [openApiSpec, setOpenApiSpec] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [parsedOperations, setParsedOperations] = useState<ParsedOperation[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResults, setImportResults] = useState<{success: number, failed: number, errors: string[]}>({
    success: 0,
    failed: 0,
    errors: []
  });
  const [expandedOperations, setExpandedOperations] = useState<Set<number>>(new Set());

  // Load agent data
  useEffect(() => {
    if (agentId) {
      AgentService.getAgent(
        Number(agentId),
        () => {},
        apiKey
      ).subscribe(
        (agentData: Agent) => {
          setAgent(agentData);
        },
        (error: any) => {
          console.error("Error loading agent:", error);
        }
      );
    }
  }, [agentId, apiKey]);

  // Reuse YAML/JSON parsing functions from APIImportModal
  const parseYaml = (yamlStr: string): any => {
    const lines = yamlStr.split("\n");
    const result: any = {};
    const stack: any[] = [{ obj: result, indent: -1 }];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      if (!trimmed || trimmed.startsWith("#")) continue;

      const indent = line.length - line.trimStart().length;

      // Handle array items
      if (trimmed.startsWith("- ")) {
        // Pop stack until we find the right parent
        while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
          stack.pop();
        }

        // Find the key that should become an array
        let arrayParent = null;
        let arrayKey = null;
        
        // First, check if we can find an empty object at the current level OR an existing array
        for (let stackIndex = stack.length - 1; stackIndex >= 0; stackIndex--) {
          const stackItem = stack[stackIndex];
          if (stackItem.indent < indent) {
            // Look through the keys of this stack level
            const keys = Object.keys(stackItem.obj);
            for (const key of keys) {
              const value = stackItem.obj[key];
              // Look for empty objects OR existing arrays
              if (value && typeof value === "object") {
                if (Array.isArray(value) || Object.keys(value).length === 0) {
                  arrayParent = stackItem;
                  arrayKey = key;
                  break;
                }
              }
            }
            if (arrayKey) break;
          }
        }
        
        // If we found a key to convert to array (or it's already an array), handle it
        if (arrayKey && arrayParent) {
          if (!Array.isArray(arrayParent.obj[arrayKey])) {
            arrayParent.obj[arrayKey] = [];
          }
        }
        
        const arrayItem = trimmed.substring(2).trim();
        
        // Create new object for this array item
        const newObj: any = {};
        
        // If array item has a colon, it's the first property of the object
        if (arrayItem.includes(":")) {
          const colonIndex = arrayItem.indexOf(":");
          const itemKey = arrayItem.substring(0, colonIndex).trim();
          let itemValue = arrayItem.substring(colonIndex + 1).trim();
          
          // Remove quotes from values
          if ((itemValue.startsWith("\"") && itemValue.endsWith("\"")) || 
              (itemValue.startsWith("'") && itemValue.endsWith("'"))) {
            itemValue = itemValue.slice(1, -1);
          }
          
          if (itemValue) {
            // Handle simple value
            if (itemValue === "true") newObj[itemKey] = true;
            else if (itemValue === "false") newObj[itemKey] = false;
            else if (/^\d+$/.test(itemValue)) newObj[itemKey] = parseInt(itemValue);
            else if (/^\d+\.\d+$/.test(itemValue)) newObj[itemKey] = parseFloat(itemValue);
            else newObj[itemKey] = itemValue;
          } else {
            newObj[itemKey] = {};
          }
        } else if (arrayItem.startsWith("[") && arrayItem.endsWith("]")) {
          // Inline array
          const arrayContent = arrayItem.slice(1, -1).trim();
          const arrayValues = arrayContent ? arrayContent.split(",").map(s => s.trim()) : [];
          if (arrayKey && arrayParent && Array.isArray(arrayParent.obj[arrayKey])) {
            arrayParent.obj[arrayKey].push(...arrayValues);
            continue;
          }
        } else if (arrayItem) {
          // Simple array item - remove quotes
          let cleanItem = arrayItem;
          if ((cleanItem.startsWith("\"") && cleanItem.endsWith("\"")) || 
              (cleanItem.startsWith("'") && cleanItem.endsWith("'"))) {
            cleanItem = cleanItem.slice(1, -1);
          }
          
          if (arrayKey && arrayParent && Array.isArray(arrayParent.obj[arrayKey])) {
            arrayParent.obj[arrayKey].push(cleanItem);
            continue;
          }
        }
        
        // Add the new object to the parent array
        if (arrayKey && arrayParent && Array.isArray(arrayParent.obj[arrayKey])) {
          arrayParent.obj[arrayKey].push(newObj);
        }
        
        // Push this object onto the stack so subsequent properties get added to it
        stack.push({ obj: newObj, indent: indent });
        continue;
      }

      // Handle regular key-value pairs
      const colonIndex = trimmed.indexOf(":");
      if (colonIndex === -1) continue;

      let key = trimmed.substring(0, colonIndex).trim();
      let value = trimmed.substring(colonIndex + 1).trim();

      // Remove quotes from keys and values
      if ((key.startsWith("\"") && key.endsWith("\"")) || 
          (key.startsWith("'") && key.endsWith("'"))) {
        key = key.slice(1, -1);
      }

      if ((value.startsWith("\"") && value.endsWith("\"")) || 
          (value.startsWith("'") && value.endsWith("'"))) {
        value = value.slice(1, -1);
      }

      // Pop stack until we find the right parent
      while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
        stack.pop();
      }

      const parent = stack[stack.length - 1];

      if (value) {
        // Handle different value types
        if (value === "true") parent.obj[key] = true;
        else if (value === "false") parent.obj[key] = false;
        else if (/^\d+$/.test(value)) parent.obj[key] = parseInt(value);
        else if (/^\d+\.\d+$/.test(value)) parent.obj[key] = parseFloat(value);
        else if (value.startsWith("[") && value.endsWith("]")) {
          // Simple array parsing
          const arrayContent = value.slice(1, -1).trim();
          const arrayItems = arrayContent ? arrayContent.split(",").map(s => {
            let item = s.trim();
            // Remove quotes from array items
            if ((item.startsWith("\"") && item.endsWith("\"")) || 
                (item.startsWith("'") && item.endsWith("'"))) {
              item = item.slice(1, -1);
            }
            return item;
          }) : [];
          parent.obj[key] = arrayItems;
        } else {
          parent.obj[key] = value;
        }
      } else {
        // Empty value means this is an object or will become an array
        parent.obj[key] = {}; // Initialize as empty object
        stack.push({ obj: parent.obj[key], indent });
      }
    }

    return result;
  };

  // Reuse reference resolution functions
  const resolveReferences = (obj: any, spec: any, visited: Set<string> = new Set()): any => {
    if (!obj || typeof obj !== "object") {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => resolveReferences(item, spec, visited));
    }

    // Check for $ref
    if (obj.$ref && typeof obj.$ref === "string") {
      // Prevent circular references
      if (visited.has(obj.$ref)) {
        console.warn(`Circular reference detected: ${obj.$ref}`);
        return { type: "object", description: `Circular reference to ${obj.$ref}` };
      }

      visited.add(obj.$ref);

      // Parse the reference path
      const refPath = obj.$ref;
      if (refPath.startsWith("#/")) {
        const pathParts = refPath.substring(2).split("/");
        let resolved = spec;
        
        for (const part of pathParts) {
          if (resolved && typeof resolved === "object" && resolved[part]) {
            resolved = resolved[part];
          } else {
            console.warn(`Could not resolve reference: ${refPath}`);
            return { type: "object", description: `Reference not found: ${refPath}` };
          }
        }

        // Recursively resolve the referenced object
        const resolvedObj = resolveReferences(resolved, spec, new Set(visited));
        visited.delete(obj.$ref);
        return resolvedObj;
      }
    }

    // Handle allOf, oneOf, anyOf
    if (obj.allOf && Array.isArray(obj.allOf)) {
      const mergedSchema: any = { type: "object", properties: {} };
      
      for (const subSchema of obj.allOf) {
        const resolvedSubSchema = resolveReferences(subSchema, spec, visited);
        
        // Merge properties
        if (resolvedSubSchema.properties) {
          mergedSchema.properties = { ...mergedSchema.properties, ...resolvedSubSchema.properties };
        }
        
        // Merge other properties
        Object.keys(resolvedSubSchema).forEach(key => {
          if (key !== "properties" && key !== "allOf") {
            mergedSchema[key] = resolvedSubSchema[key];
          }
        });
        
        // Merge required arrays
        if (resolvedSubSchema.required && Array.isArray(resolvedSubSchema.required)) {
          if (!mergedSchema.required) mergedSchema.required = [];
          mergedSchema.required = Array.from(new Set([...mergedSchema.required, ...resolvedSubSchema.required]));
        }
      }
      
      // Merge any additional properties from the current object
      Object.keys(obj).forEach(key => {
        if (key !== "allOf") {
          if (key === "properties") {
            mergedSchema.properties = { ...mergedSchema.properties, ...obj[key] };
          } else if (key === "required" && Array.isArray(obj[key])) {
            if (!mergedSchema.required) mergedSchema.required = [];
            mergedSchema.required = Array.from(new Set([...mergedSchema.required, ...obj[key]]));
          } else {
            mergedSchema[key] = obj[key];
          }
        }
      });
      
      return mergedSchema;
    }

    // Recursively resolve nested objects
    const result: any = {};
    for (const [key, value] of Object.entries(obj)) {
      result[key] = resolveReferences(value, spec, visited);
    }

    return result;
  };

  const convertOpenAPISchemaToSchemaProperty = (schema: any, name: string = "", description: string = "", fullSpec?: any): SchemaProperty => {
    if (!schema || typeof schema !== "object") {
      return {
        name: name || "property",
        type: "string",
        description: description || ""
      };
    }

    // Resolve references if we have the full spec
    let resolvedSchema = schema;
    if (fullSpec && (schema.$ref || schema.allOf)) {
      resolvedSchema = resolveReferences(schema, fullSpec);
    }

    const schemaProperty: SchemaProperty = {
      name: name || "property",
      type: resolvedSchema.type || "string",
      description: description || resolvedSchema.description || "",
      required: false
    };

    // Handle format
    if (resolvedSchema.format) {
      schemaProperty.format = resolvedSchema.format;
    }

    // Handle enum
    if (resolvedSchema.enum && Array.isArray(resolvedSchema.enum)) {
      schemaProperty.enum = resolvedSchema.enum;
    }

    // Handle object properties
    if (resolvedSchema.type === "object" && resolvedSchema.properties && typeof resolvedSchema.properties === "object" && resolvedSchema.properties !== null) {
      schemaProperty.properties = {};
      Object.entries(resolvedSchema.properties).forEach(([propName, propSchema]: [string, any]) => {
        const isRequired = resolvedSchema.required && Array.isArray(resolvedSchema.required) && resolvedSchema.required.includes(propName);
        const convertedProp = convertOpenAPISchemaToSchemaProperty(propSchema, propName, propSchema?.description, fullSpec);
        convertedProp.required = isRequired;
        schemaProperty.properties![propName] = convertedProp;
      });
    }

    // Handle array items
    if (resolvedSchema.type === "array" && resolvedSchema.items) {
      schemaProperty.items = convertOpenAPISchemaToSchemaProperty(resolvedSchema.items, "item", resolvedSchema.items?.description, fullSpec);
    }

    return schemaProperty;
  };

  const convertSchemaToProperties = (schema: any, fullSpec?: any): { [key: string]: SchemaProperty } => {
    if (!schema) {
      return {};
    }

    // Resolve references if we have the full spec
    let resolvedSchema = schema;
    if (fullSpec && (schema.$ref || schema.allOf)) {
      resolvedSchema = resolveReferences(schema, fullSpec);
    }

    // If we have properties, process them normally
    if (resolvedSchema.properties && typeof resolvedSchema.properties === "object") {
      const properties: { [key: string]: SchemaProperty } = {};
      Object.entries(resolvedSchema.properties).forEach(([propName, propSchema]: [string, any]) => {
        const isRequired = resolvedSchema.required && Array.isArray(resolvedSchema.required) && resolvedSchema.required.includes(propName);
        const convertedProp = convertOpenAPISchemaToSchemaProperty(propSchema, propName, propSchema?.description, fullSpec);
        convertedProp.required = isRequired;
        properties[propName] = convertedProp;
      });
      return properties;
    }

    // If the resolved schema doesn't have properties but has other schema info,
    // create a single property representing the entire response
    if (resolvedSchema.type || resolvedSchema.$ref) {
      return {
        "value": convertOpenAPISchemaToSchemaProperty(resolvedSchema, "value", resolvedSchema.description || "Response value", fullSpec)
      };
    }

    return {};
  };

  const parseOpenAPISpec = () => {
    setIsLoading(true);
    setError(null);
    
    try {
      let spec: any;
      
      // Try to parse as YAML first, then JSON
      try {
        if (openApiSpec.trim().startsWith("{")) {
          spec = JSON.parse(openApiSpec);
        } else {
          spec = parseYaml(openApiSpec);
        }
      } catch (yamlError) {
        // If YAML parsing fails, try JSON
        try {
          spec = JSON.parse(openApiSpec);
        } catch (jsonError) {
          console.error("Parsing errors - YAML:", yamlError, "JSON:", jsonError);
          throw new Error(`Failed to parse specification as YAML or JSON.

YAML Error: ${yamlError instanceof Error ? yamlError.message : String(yamlError)}
JSON Error: ${jsonError instanceof Error ? jsonError.message : String(jsonError)}

Suggestions:
• Verify your YAML indentation is correct
• Ensure all strings are properly quoted
• Try converting to JSON format for better compatibility`);
        }
      }

      const operations: ParsedOperation[] = [];

      console.log("Parsed spec - has paths:", !!spec.paths);
      console.log("Parsed spec - has components:", !!spec.components);

      // Check if components.schemas exists
      if (spec.components && spec.components.schemas) {
        console.log("Found components.schemas:", Object.keys(spec.components.schemas));
      }

      // Detect if this is a full spec, paths object, or single operation
      if (spec.paths) {
        // Full OpenAPI spec
        Object.entries(spec.paths).forEach(([path, pathItem]: [string, any]) => {
          Object.entries(pathItem).forEach(([method, operation]: [string, any]) => {
            if (["get", "post", "put", "patch", "delete", "head", "options", "trace"].includes(method.toLowerCase())) {
              const actionName = operation.operationId || 
                              operation.summary || 
                              `${method.toUpperCase()}_${path.replace(/[^\w]/g, "_")}`;
              
              const description = operation.description || 
                                operation.summary || 
                                `${method.toUpperCase()} ${path}`;

              operations.push({
                method: method.toLowerCase(),
                path,
                operationId: operation.operationId,
                summary: operation.summary,
                description: operation.description,
                parameters: operation.parameters,
                requestBody: operation.requestBody,
                responses: operation.responses,
                selected: true, // Default to selected
                actionName,
                actionDescription: description
              });
            }
          });
        });
      }

      if (operations.length === 0) {
        throw new Error("No valid operations found in the OpenAPI specification. Please ensure your spec contains at least one operation with a valid HTTP method (GET, POST, etc.).");
      }

      setParsedOperations(operations);
      
    } catch (err) {
      console.error("Parse error:", err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    setParsedOperations(ops => ops.map(op => ({ ...op, selected: checked })));
  };

  const handleOperationToggle = (index: number, checked: boolean) => {
    setParsedOperations(ops => ops.map((op, i) => i === index ? { ...op, selected: checked } : op));
  };

  const handleOperationNameChange = (index: number, name: string) => {
    setParsedOperations(ops => ops.map((op, i) => i === index ? { ...op, actionName: name } : op));
  };

  const handleOperationDescriptionChange = (index: number, description: string) => {
    setParsedOperations(ops => ops.map((op, i) => i === index ? { ...op, actionDescription: description } : op));
  };

  const toggleOperationExpanded = (index: number) => {
    setExpandedOperations(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleBulkImport = async () => {
    if (!agentId) return;

    const selectedOperations = parsedOperations.filter(op => op.selected);
    if (selectedOperations.length === 0) {
      setError("Please select at least one operation to import.");
      return;
    }

    setIsImporting(true);
    setImportResults({ success: 0, failed: 0, errors: [] });

    // Get the full spec for reference resolution
    let fullSpec: any = null;
    try {
      if (openApiSpec.trim().startsWith("{")) {
        fullSpec = JSON.parse(openApiSpec);
      } else {
        fullSpec = parseYaml(openApiSpec);
      }
    } catch (err) {
      console.warn("Could not parse full spec for reference resolution:", err);
    }

    let successCount = 0;
    let failedCount = 0;
    const errors: string[] = [];

    for (const operation of selectedOperations) {
      try {
        const config: APIActionConfig = {
          endpoint: operation.path,
          method: operation.method.toUpperCase(),
          headers: { "Content-Type": "application/json" },
          timeout: 30,
          parameters: [],
          requestSchema: {},
          responseSchemas: {},
          auth: {
            type: "none",
            token: "",
            header: "Authorization",
            key: "",
          }
        };

        // Convert parameters
        if (operation.parameters && Array.isArray(operation.parameters)) {
          config.parameters = operation.parameters
            .filter(param => param.in === "path" || param.in === "query")
            .map(param => ({
              name: param.name,
              in: param.in as "path" | "query",
              type: param.schema?.type || "string",
              required: param.required || false,
              description: param.description,
              defaultValue: param.schema?.default,
              enum: param.schema?.enum
            }));
        }

        // Convert request body
        if (operation.requestBody && 
            operation.requestBody.content && 
            operation.requestBody.content["application/json"] &&
            operation.requestBody.content["application/json"].schema) {
          const schema = operation.requestBody.content["application/json"].schema;
          config.requestSchema = convertSchemaToProperties(schema, fullSpec);
        }

        // Convert response schemas
        if (operation.responses && typeof operation.responses === "object") {
          Object.entries(operation.responses).forEach(([statusCode, response]: [string, any]) => {
            if (response.content && 
                response.content["application/json"] && 
                response.content["application/json"].schema) {
              const schema = response.content["application/json"].schema;
              config.responseSchemas![statusCode] = convertSchemaToProperties(schema, fullSpec);
            }
          });
        }

        // Create the agent action
        const actionData = {
          action_name: operation.actionName || `${operation.method}_${operation.path}`,
          action_type: "api_call",
          description: operation.actionDescription || `${operation.method.toUpperCase()} ${operation.path}`,
          action_config: config
        };

        // Use AgentActionService to create the action
        await new Promise((resolve, reject) => {
          AgentActionService.createAgentAction(
            Number(agentId),
            actionData,
            () => {},
            apiKey
          ).subscribe(
            (result: any) => {
              successCount++;
              resolve(result);
            },
            (error: any) => {
              failedCount++;
              errors.push(`${operation.actionName}: ${error.message || error}`);
              reject(error);
            }
          );
        });

      } catch (error) {
        failedCount++;
        errors.push(`${operation.actionName}: ${error instanceof Error ? error.message : String(error)}`);
      }
    }

    setImportResults({
      success: successCount,
      failed: failedCount,
      errors
    });
    setIsImporting(false);
  };

  const selectedCount = parsedOperations.filter(op => op.selected).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge/agents")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Agents
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/agent/${agentId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    {agent?.agent_name}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Bulk Import</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Bulk API Import
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Import multiple API actions from an OpenAPI specification
                </p>
              </div>
              <Button
                onClick={() => navigate(`/agent/${agentId}?tab=capabilities`)}
                variant="outline"
                className="gap-2"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Agent
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Step 1: Paste OpenAPI Spec */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-maitai-green text-white text-sm font-gc-med flex items-center justify-center">1</span>
              Paste OpenAPI Specification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">OpenAPI Spec (JSON or YAML)</Label>
              <Button
                onClick={parseOpenAPISpec}
                disabled={!openApiSpec.trim() || isLoading}
                className="gap-2"
              >
                <UploadIcon className="w-4 h-4" />
                {isLoading ? "Parsing..." : "Parse Spec"}
              </Button>
            </div>
            <Textarea
              value={openApiSpec}
              onChange={(e) => setOpenApiSpec(e.target.value)}
              placeholder={`Paste your OpenAPI specification here...

Example:
openapi: 3.0.3
info:
  title: My API
  version: 1.0.0
paths:
  /users:
    get:
      summary: List users
      responses:
        '200':
          description: Success
components:
  schemas:
    User:
      type: object
      properties:
        id:
          type: string
        name:
          type: string`}
              className="min-h-[300px] font-mono text-sm"
            />
          </CardContent>
        </Card>

        {/* Error Display */}
        {error && (
          <Card className="border-red-200 bg-red-50 dark:bg-red-900/20">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertCircleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">Parsing Error</p>
                  <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Operations */}
        {parsedOperations.length > 0 && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-maitai-green text-white text-sm font-gc-med flex items-center justify-center">2</span>
                Select Operations to Import
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="select-all"
                      checked={selectedCount === parsedOperations.length}
                      onCheckedChange={handleSelectAll}
                    />
                    <Label htmlFor="select-all" className="text-sm font-medium">
                      Select All ({parsedOperations.length} operations)
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {selectedCount} of {parsedOperations.length} selected
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedOperations(new Set())}
                    className="text-xs"
                  >
                    Collapse All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpandedOperations(new Set(parsedOperations.map((_, i) => i)))}
                    className="text-xs"
                  >
                    Expand All
                  </Button>
                </div>
              </div>

              <div className="space-y-4 max-h-96 overflow-y-auto">
                {parsedOperations.map((operation, index) => {
                  const isExpanded = expandedOperations.has(index);
                  return (
                    <div
                      key={index}
                      className={`border rounded-lg ${
                        operation.selected
                          ? "border-maitai-green bg-maitai-green/5 dark:bg-maitai-green/10"
                          : "border-border"
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={operation.selected}
                            onCheckedChange={(checked) => handleOperationToggle(index, checked as boolean)}
                            className="mt-1"
                          />
                          <div className="flex-1 space-y-3">
                            {/* Method, Path, and Summary */}
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <span className={`px-2 py-1 rounded text-xs font-mono font-medium ${
                                  operation.method === "get" ? "bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300" :
                                  operation.method === "post" ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" :
                                  operation.method === "put" ? "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300" :
                                  operation.method === "patch" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300" :
                                  operation.method === "delete" ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300" :
                                  "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                                }`}>
                                  {operation.method.toUpperCase()}
                                </span>
                                <span className="font-mono text-sm text-foreground">{operation.path}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleOperationExpanded(index)}
                                  className="ml-auto p-1 h-6 w-6"
                                >
                                  {isExpanded ? (
                                    <ChevronDownIcon className="w-4 h-4" />
                                  ) : (
                                    <ChevronRightIcon className="w-4 h-4" />
                                  )}
                                </Button>
                              </div>
                              {operation.summary && (
                                <p className="text-sm text-muted-foreground pl-2 border-l-2 border-muted-foreground/20">
                                  {operation.summary}
                                </p>
                              )}
                            </div>
                            
                            {/* Action Name and Description */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-sm font-medium">Action Name</Label>
                                <Input
                                  value={operation.actionName || ""}
                                  onChange={(e) => handleOperationNameChange(index, e.target.value)}
                                  placeholder="Action name"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-sm font-medium">Description</Label>
                                <Input
                                  value={operation.actionDescription || ""}
                                  onChange={(e) => handleOperationDescriptionChange(index, e.target.value)}
                                  placeholder="Action description"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expandable API Details */}
                      {isExpanded && (
                        <div className="border-t border-border p-4 bg-muted/20">
                          <div className="space-y-4">
                            <h4 className="font-medium text-sm text-foreground">API Details</h4>
                            
                            {/* Operation Description */}
                            {operation.description && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
                                <p className="text-sm text-foreground">{operation.description}</p>
                              </div>
                            )}

                            {/* Operation ID */}
                            {operation.operationId && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Operation ID</p>
                                <code className="text-sm bg-muted px-2 py-1 rounded">{operation.operationId}</code>
                              </div>
                            )}

                            {/* Parameters */}
                            {operation.parameters && operation.parameters.length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">Parameters ({operation.parameters.length})</p>
                                <div className="space-y-2">
                                  {operation.parameters.map((param: any, paramIndex: number) => (
                                    <div key={paramIndex} className="flex items-center gap-2 text-sm">
                                      <span className="font-mono bg-muted px-2 py-1 rounded text-xs">
                                        {param.name}
                                      </span>
                                      <span className="text-muted-foreground">in {param.in}</span>
                                      <span className="text-muted-foreground">({param.schema?.type || "string"})</span>
                                      {param.required && (
                                        <span className="text-red-600 text-xs">required</span>
                                      )}
                                      {param.description && (
                                        <span className="text-muted-foreground flex-1 text-xs">- {param.description}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Request Body */}
                            {operation.requestBody && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">Request Body</p>
                                <div className="text-sm text-foreground">
                                  {operation.requestBody.content && (
                                    <div className="space-y-1">
                                      {Object.keys(operation.requestBody.content).map((contentType) => (
                                        <div key={contentType} className="flex items-center gap-2">
                                          <code className="bg-muted px-2 py-1 rounded text-xs">{contentType}</code>
                                          {operation.requestBody.required && (
                                            <span className="text-red-600 text-xs">required</span>
                                          )}
                                        </div>
                                      ))}
                                      {operation.requestBody.description && (
                                        <p className="text-muted-foreground text-xs mt-1">{operation.requestBody.description}</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Responses */}
                            {operation.responses && Object.keys(operation.responses).length > 0 && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-2">Responses ({Object.keys(operation.responses).length})</p>
                                <div className="space-y-2">
                                  {Object.entries(operation.responses).map(([statusCode, response]: [string, any]) => (
                                    <div key={statusCode} className="flex items-start gap-2 text-sm">
                                      <span className={`px-2 py-1 rounded text-xs font-mono ${
                                        statusCode.startsWith("2") ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300" :
                                        statusCode.startsWith("4") ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300" :
                                        statusCode.startsWith("5") ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300" :
                                        "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300"
                                      }`}>
                                        {statusCode}
                                      </span>
                                      <div className="flex-1">
                                        <p className="text-foreground">{response.description || "No description"}</p>
                                        {response.content && (
                                          <div className="mt-1">
                                            {Object.keys(response.content).map((contentType) => (
                                              <code key={contentType} className="bg-muted px-1 py-0.5 rounded text-xs mr-2">
                                                {contentType}
                                              </code>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Import */}
        {parsedOperations.length > 0 && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-maitai-green text-white text-sm font-gc-med flex items-center justify-center">3</span>
                Import Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Ready to import {selectedCount} action{selectedCount !== 1 ? "s" : ""} to <strong>{agent?.agent_name}</strong>
                </p>
                <Button
                  onClick={handleBulkImport}
                  disabled={selectedCount === 0 || isImporting}
                  className="gap-2"
                >
                  <UploadIcon className="w-4 h-4" />
                  {isImporting ? "Importing..." : `Import ${selectedCount} Action${selectedCount !== 1 ? "s" : ""}`}
                </Button>
              </div>

              {/* Import Results */}
              {(importResults.success > 0 || importResults.failed > 0) && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <h4 className="font-medium">Import Results</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon className="w-4 h-4 text-green-600" />
                      <span>{importResults.success} imported successfully</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <AlertCircleIcon className="w-4 h-4 text-red-600" />
                      <span>{importResults.failed} failed to import</span>
                    </div>
                  </div>
                  
                  {importResults.errors.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-red-600">Errors:</p>
                      <ul className="text-sm text-red-600 space-y-1">
                        {importResults.errors.map((error, index) => (
                          <li key={index} className="ml-4">• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importResults.success > 0 && (
                    <Button
                      onClick={() => navigate(`/agent/${agentId}?tab=capabilities`)}
                      className="gap-2"
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      View Imported Actions
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BulkAPIImportPage; 